import React, {useState} from 'react';
import {Button, Modal, Spinner } from "react-bootstrap";
import { ChargingBillingCycle, ChargingAgreement } from '../models';
import NewChargingAgreementData from '../components/NewChargingAgreement/NewChargingAgreementData';
import { ChargingAgreementsService } from '../ChargingAgreements.service';
import { ChargingPricing } from 'components/ChargingPricings/models';
import { ModalProps } from 'common/common-models';
import { isNotNullOrUndefined } from 'common/data-util';
import { isEmailValidAndPresent, validateCommaSeparatedPersonalIdentificationCodes } from 'helpers/validator';
import { PERSONAL_CODE_LENGTH } from 'common/constants';

export enum EditChargingAgreementModalType {
    NEW,
    EDIT,
    OFFER
}

interface IEditChargingAgreementModalProps extends Omit<ModalProps, "modalType"> {
    chargingAgreement?: ChargingAgreement;
    modalType: EditChargingAgreementModalType
    isOffer?: boolean;
}

export interface CreateChargingAgreementForm {
    validated: boolean;
    customer?: ChargingAgreementCustomerForm;
    address?: string;
    city?: string;
    zip?: string;
    externalChargingStationId?: string;
    meteringPoint?: ChargingAgreementMeteringPointForm;
    purchaseOptionId?: number;
    serviceOptionId?: number;
    thirdPartyCompensationOptionId?: number;
    chargingStations: ChargingAgreementStationForm[]
    pricingId?: number;
    termsAndConditionsId?: number;
    contractPeriodId?: number;
    extensionContractPeriodId?: number;
    startDate?: Date;
    endDate?: Date;
    offerDeadline?: Date;
    hasEmergencyMaintenanceService: boolean;
    hasChargerMaintenanceService: boolean;
    chargerMaintenanceType?: ChargingBillingCycle;
    simCardCount?: number;
    simCardPaymentType?: ChargingBillingCycle;
    rfidPaymentCardCount?: number;
    advancePaymentEuros?: number;
    createCompensationTable: boolean;
    includePricesInCompensationTable: boolean;
    installationFeeEuros?: number;
    devicesRentalMonthlyFeeEuros?: number;
    devicesFullServiceRentalMonthlyFeeEuros?: number;
    devicesCostEuros?: number;
    thirdPartyCompensationNetoFeeEuros?: number;
    thirdPartyCompensationNetoMarginalCentsKwh?: number;
    marketPriceChargingPersonalIdentificationCodes?: string[];
    maintenanceMonthlyFeeEuros?: number;
    rfidPaymentCardMonthlyFeeEuros?: number;
    simCardMonthlyFeeEuros?: number;
    mediationAndBillingMonthlyFeePerNozzleEuros?: number;
    infrastructureUsageOneTimeFeePerNozzleEuros?: number;
    infrastructureUsageMonthlyFeePerNozzleEuros?: number;
    chargingServiceMonthlyFeeEuros?: number;
    thirdPartyChargingMarginalCentsKwh?: number;
    gridDayTariffCentsKwh?: number;
    gridNightTariffCentsKwh?: number;
    gridExciseDutyCentsKwh?: number;
    gridRenewableEnergyChargeCentsKwh?: number;
    acPublicChargingPriceEurosKwh?: number;
}

export interface ChargingAgreementMeteringPointForm {
    id?: number;
    eic?: string;
    address?: string;
    productionCapacity?: number;
}

export interface ChargingAgreementMeteringPointForm {
    id?: number;
    eic?: string;
    address?: string;
    productionCapacity?: number;
}

export interface ChargingAgreementCustomerForm {
    id?: number;
    eic?: string;
    isPerson?: boolean;
    apartmentAssociation?: boolean;
    customerName?: string;
    registryNumber?: string;
    vatRegistryNumber?: string;
    contactName?: string;
    contactPersonId?: number;
    phone?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    contactPersonCode?: string;
    address?: string;
    city?: string;
    zip?: string;
    county?: string;
}

export interface ChargingAgreementStationForm {
    serialNumber?: string;
    latitude?: string;
    longitude?: string;
    installationDate?: Date;
    installationExtraFeeEuros?: number;
    nozzleCount?: number;
    stationModelId?: number;
    externalId?: string;
    thirdPartyCompensationOptionId?: number;
    marketPriceChargingPersonalIdentificationCodes?: string[];
    personalIdentificationCode?: string;
}

export const MARKET_PRICE_COMPENSATION_ID = 4;
export const NETO_COMPENSATION_ID = 3;
export const FULL_SERVICE_RENT_PURCHASE_OPTION_ID = 3;
export const RENT_PURCHASE_OPTION_ID = 2;

export function EditChargingAgreementModal(props: IEditChargingAgreementModalProps) {
    const [saving, setSaving]: [boolean, any] = useState(false);
    const [savingError, setSavingError]: [boolean, any] = useState(false);
    const [form, setForm]: [CreateChargingAgreementForm, any] = useState({
        validated: false,
        customer: props.chargingAgreement ? {
            id: props.chargingAgreement.customer.id,
            eic: props.chargingAgreement.customer.eic,
            contactPersonId: props.chargingAgreement.contactPerson.id,
            contactName: props.chargingAgreement.contactPerson.name,
            email: props.chargingAgreement.contactPerson.email,
            phone: props.chargingAgreement.contactPerson.phone,
            registryNumber: props.chargingAgreement.customer.registryNumber
        } : undefined,
        hasEmergencyMaintenanceService: props.chargingAgreement?.hasEmergencyMaintenanceService ?? false,
        hasChargerMaintenanceService: props.chargingAgreement?.hasChargerMaintenanceService ?? false,
        simCardCount: props.chargingAgreement?.simCardCount,
        rfidPaymentCardCount: props.chargingAgreement?.rfidPaymentCardCount,
        chargingStations: props.chargingAgreement ? props.chargingAgreement.chargingStations.map(station => ({
            ...station,
            stationModelId: station.model.id,
            thirdPartyCompensationOptionId: station.thirdPartyCompensationOption?.id,
            personalIdentificationCode: station?.personalIdentificationCode
        }) as ChargingAgreementStationForm) : [],
        meteringPoint: props.chargingAgreement?.meteringPoint,
        address: props.chargingAgreement?.address,
        city: props.chargingAgreement?.city,
        zip: props.chargingAgreement?.zip,
        externalChargingStationId: props.chargingAgreement?.externalChargingStationId,
        purchaseOptionId: props.chargingAgreement?.purchaseOption.id,
        serviceOptionId: props.chargingAgreement?.serviceOption.id,
        thirdPartyCompensationOptionId: props.chargingAgreement?.thirdPartyCompensationOption?.id,
        marketPriceChargingPersonalIdentificationCodes: props.chargingAgreement?.marketPriceChargingPersonalIdentificationCodes,
        extensionContractPeriodId: props.chargingAgreement?.extensionContractPeriod?.id ?? undefined,
        contractPeriodId: props.chargingAgreement?.contractPeriod.id,
        termsAndConditionsId: props.chargingAgreement?.termsAndConditions.id,
        startDate: props.chargingAgreement?.startDate ? new Date(props.chargingAgreement?.startDate) : undefined,
        endDate: props.chargingAgreement?.endDate ? new Date(props.chargingAgreement?.endDate) : undefined,
        chargerMaintenanceType: props.chargingAgreement?.chargerMaintenanceType,
        simCardPaymentType: props.chargingAgreement?.simCardPaymentType,
        advancePaymentEuros: props.chargingAgreement?.advancePaymentEuros,
        createCompensationTable: props.chargingAgreement?.createCompensationTable ?? false,
        includePricesInCompensationTable: props.chargingAgreement?.includePricesInCompensationTable ?? false,
        installationFeeEuros: props.chargingAgreement?.pricing.installationFeeEuros,
        devicesRentalMonthlyFeeEuros: props.chargingAgreement?.pricing.devicesRentalMonthlyFeeEuros,
        devicesFullServiceRentalMonthlyFeeEuros: props.chargingAgreement?.pricing.devicesFullServiceRentalMonthlyFeeEuros,
        devicesCostEuros: props.chargingAgreement?.pricing.devicesCostEuros,
        thirdPartyCompensationNetoFeeEuros: props.chargingAgreement?.pricing.thirdPartyCompensationNetoFeeEuros,
        thirdPartyCompensationNetoMarginalCentsKwh: props.chargingAgreement?.pricing?.thirdPartyCompensationNetoMarginalCentsKwh,
        maintenanceMonthlyFeeEuros: props.chargingAgreement?.pricing.maintenanceMonthlyFeeEuros,
        rfidPaymentCardMonthlyFeeEuros: props.chargingAgreement?.pricing.rfidPaymentCardMonthlyFeeEuros,
        simCardMonthlyFeeEuros: props.chargingAgreement?.pricing.simCardMonthlyFeeEuros,
        mediationAndBillingMonthlyFeePerNozzleEuros: props.chargingAgreement?.pricing.mediationAndBillingMonthlyFeePerNozzleEuros,
        infrastructureUsageOneTimeFeePerNozzleEuros: props.chargingAgreement?.pricing.infrastructureUsageOneTimeFeePerNozzleEuros,
        infrastructureUsageMonthlyFeePerNozzleEuros: props.chargingAgreement?.pricing.infrastructureUsageMonthlyFeePerNozzleEuros,
        chargingServiceMonthlyFeeEuros: props.chargingAgreement?.pricing.chargingServiceMonthlyFeeEuros,
        thirdPartyChargingMarginalCentsKwh: props.chargingAgreement?.pricing.thirdPartyChargingMarginalCentsKwh,
        gridDayTariffCentsKwh: props.chargingAgreement?.pricing.gridDayTariffCentsKwh,
        gridNightTariffCentsKwh: props.chargingAgreement?.pricing.gridNightTariffCentsKwh,
        gridExciseDutyCentsKwh: props.chargingAgreement?.pricing.gridExciseDutyCentsKwh,
        gridRenewableEnergyChargeCentsKwh: props.chargingAgreement?.pricing.gridRenewableEnergyChargeCentsKwh,
        acPublicChargingPriceEurosKwh: props.chargingAgreement?.pricing.acPublicChargingPriceEurosKwh
    });

    const [pricing, setPricing] = useState<ChargingPricing>();

    const isEditModal = props.modalType === EditChargingAgreementModalType.EDIT;
    const isOfferModal = props.modalType === EditChargingAgreementModalType.OFFER;
    const chargerBasedConfig = (!form.customer?.isPerson || form.customer?.apartmentAssociation) ?? false;

    const validateSubmit = () => {
        if (validate()) {
            submit();
        }
    }

    const validateCustomer = () => {
        if (!form.customer?.registryNumber || !form.customer?.address || !isEmailValidAndPresent(form.customer.email) || !form.customer?.phone) return false;
        return true;
    }

    const validateMeteringPoint = () => {
        if(!form.meteringPoint?.eic || !form.meteringPoint.address) return false;
        if(!chargerBasedConfig && !form.externalChargingStationId) return false;
        return true;
    }

    const validateDevices = () => {
        if(!form.purchaseOptionId) return false;
        if(!chargerBasedConfig && !form.thirdPartyCompensationOptionId) return false;
        if(form.purchaseOptionId === FULL_SERVICE_RENT_PURCHASE_OPTION_ID && !isNotNullOrUndefined(form.devicesFullServiceRentalMonthlyFeeEuros)) return false;
        if(form.purchaseOptionId === RENT_PURCHASE_OPTION_ID && !isNotNullOrUndefined(form.devicesRentalMonthlyFeeEuros)) return false;

        return true;
    }

    const validateServices = () => {
        if(!form.serviceOptionId) return false;
        if(form.hasChargerMaintenanceService && (!form.chargerMaintenanceType || !isNotNullOrUndefined(form.maintenanceMonthlyFeeEuros))) return false;
        if(form.simCardPaymentType && !isNotNullOrUndefined(form.simCardMonthlyFeeEuros)) return false;

        return true;
    }

    const validateChargingStations = () => {
        if(chargerBasedConfig && form.chargingStations.length === 0) return false;
        return form.chargingStations.every(chargingStation => {
            if(!chargingStation.serialNumber || !chargingStation.stationModelId) return false;
            if(isNotNullOrUndefined(chargingStation.personalIdentificationCode) && chargingStation.personalIdentificationCode.length !== PERSONAL_CODE_LENGTH) return false;
            if(chargerBasedConfig){
                if(!chargingStation.externalId) return false;
                if(!chargingStation.thirdPartyCompensationOptionId) return false;
                if(!validateCommaSeparatedPersonalIdentificationCodes(chargingStation.marketPriceChargingPersonalIdentificationCodes)) return false;
            }
            return true;
        });
    }

    const validateAgreement = () => {
        if((!isEditModal && !form.pricingId) || !form.termsAndConditionsId || !form.contractPeriodId || (!isOfferModal && !form.startDate) || (isOfferModal && !form.offerDeadline)) return false;

        return true;
    }

    const validate = () => {
        setForm({ ...form, validated: true });

        return validateCustomer() && validateMeteringPoint() && validateDevices() &&
            validateServices() && validateChargingStations() && validateAgreement() &&
            (chargerBasedConfig || validateCommaSeparatedPersonalIdentificationCodes(form.marketPriceChargingPersonalIdentificationCodes));
    }

    const submit = async () => {
        try{
            setSaving(true);
            if(isEditModal){
                await ChargingAgreementsService.updateChargingAgreement(form, props.chargingAgreement!.id);
            }else if (isOfferModal){
                await ChargingAgreementsService.createChargingAgreementOffer(form);
            }else{
                await ChargingAgreementsService.createChargingAgreement(form);
            }
            setSavingError(false);      
            setSaving(false);
            props.handleModalHide();
            props.onSuccess?.()
        }catch(e){
            console.error(e);
            setSaving(false);
            setSavingError(true);
        }
    };

    const getModalTitle = () => {             
        switch(props.modalType){
            case EditChargingAgreementModalType.NEW:
                return "Laadimislepingu loomine";
            case EditChargingAgreementModalType.OFFER:
                return "Pakkumise loomine";
            case EditChargingAgreementModalType.EDIT:
                return "Laadimislepingu uuendamine";
        }
    }

    return (
        <Modal
            show={true}
            size={"xl"}
            dialogClassName="bg-light"
            onHide={props.handleModalHide}
            backdrop="static"
        >
            <Modal.Header>
                <Modal.Title>
                    <span>
                        {getModalTitle()}
                    </span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <NewChargingAgreementData
                    form={form}
                    setForm={setForm}
                    pricing={pricing}
                    setPricing={setPricing}
                    isEditModal={isEditModal}
                    isOfferModal={isOfferModal}
                    chargerBasedConfig={chargerBasedConfig}
                />
            </Modal.Body>

            <Modal.Footer className="d-flex flex-row align-items-end justify-content-around flex-wrap mb-1">
                <Button variant="info"
                        onClick={() => props.handleModalHide()}
                        type="button"
                        className={"d-flex align-items-center"}>
                    <span className='px-2'>Tühista</span>
                </Button>
                <Button variant="primary" onClick={validateSubmit} type="button" disabled={saving}
                        className="d-flex align-items-center">
                    {
                        !saving &&
                        <span className='px-2'>Salvesta</span>
                    }
                    {
                        saving &&
                        <>
                            <span className='px-2'>Salvestab…</span>
                            <Spinner size={"sm"} animation={"border"}/>
                        </>
                    }
                </Button>

            </Modal.Footer>
            {
                !saving && savingError &&
                <span className="d-flex justify-content-end text-danger px-2 pb-2">
                    Viga salvestamisel, proovi mõne hetke pärast uuesti.
                </span>
            }
        </Modal>
    );
}
